import { useUserInfo } from "@/hooks/member/useMember";
import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../ui/Button";
import ChangePasswordModal from "@/components/modal/system/changePassword/Change";
import MyMenuModal from "@/components/modal/setting/se_company/MyMenuModal";
import { useMenuList, useMyMenuList } from "@/hooks/menu/useMenu";
import { Menu, MenuWithFavorite, MyMenuResponse } from "@/interface/menu";
import { useExpiringDrivers } from "@/hooks/standard/st_driver_code/useDriverCode";
import { DriverCode } from "@/interface/standard/st_driver_code";
import { formatDate } from "@/lib/date";

type MenuModalKey = "changePassword" | "myMenu";

export function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: userInfo } = useUserInfo();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [openChangePw, setOpenChangePw] = useState(false);
  const [openMyMenu, setOpenMyMenu] = useState(false);
  const [openAlarmDropdown, setOpenAlarmDropdown] = useState(false);
  const alarmDropdownRef = useRef<HTMLDivElement>(null);

  const { data: menuList } = useMenuList();
  const { data: expiringDriversData } = useExpiringDrivers();
  const { data: myMenuResponse } = useMyMenuList(userInfo?.name || "") as { data: MyMenuResponse | undefined };

  // 하위 메뉴 생성을 위한 공통 함수
  const createSubMenu = (menu: { name: string; path: string | null; isActive: boolean }) => {
    const modals: MenuModalKey[] = [];

    if (menu.path === "/system/changePassword") {
      modals.push("changePassword");
    }
    if (menu.path === "/system/myMenu") {
      modals.push("myMenu");
    }

    return {
      name: menu.name,
      path: menu.path || "",
      isComingSoon: !menu.isActive,
      modal: modals.length > 0 ? modals : undefined,
    };
  };

  // DB에서 받아온 메뉴 데이터를 컴포넌트에서 사용할 수 있는 형태로 변환
  const menuItems =
    menuList
      ?.map((menu: Menu) => {
        // "즐겨찾기" 메뉴인 경우, 즐겨찾기 메뉴로 교체
        if (menu.name === "즐겨찾기" && myMenuResponse?.right && myMenuResponse.right.length > 0) {
          const favoriteSubMenus = myMenuResponse.right.map(favMenu => createSubMenu(favMenu));

          return {
            name: menu.name,
            path: menu.path || "",
            subMenus: favoriteSubMenus,
          };
        }

        // 권한이 있는 하위 메뉴만 필터링
        const isSuperAdmin = myMenuResponse?.role === "SUPER_ADMIN";
        const subMenus =
          menu.children
            ?.map(child => {
              // myMenuResponse.all에서 해당 메뉴 찾기
              const permMenu = myMenuResponse?.all?.find(pm => pm.path === child.path);
              const hasPermission = permMenu?.canRead;

              // adminOnly 메뉴는 SUPER_ADMIN만 접근 가능
              if (permMenu?.adminOnly && !isSuperAdmin) {
                return null;
              }

              // 권한이 있는 메뉴만 반환
              return hasPermission ? createSubMenu(child) : null;
            })
            .filter((subMenu): subMenu is NonNullable<typeof subMenu> => subMenu !== null) || [];

        return {
          name: menu.name,
          path: menu.path || "",
          subMenus,
        };
      })
      // 하위 메뉴가 있거나, 단독 메뉴(path가 있는 경우)도 표시
      .filter(item => item.name === "즐겨찾기" || (item.subMenus && item.subMenus.length > 0) || item.path) || [];

  // 현재 경로가 메뉴나 하위 메뉴에 포함되는지 확인
  const isMenuActive = (item: (typeof menuItems)[0]) => {
    // "즐겨찾기" 메뉴는 활성화하지 않음
    if (item.name === "즐겨찾기") {
      return false;
    }
    return location.pathname === item.path || item.subMenus?.some(sub => location.pathname === sub.path);
  };

  // 드롭다운 토글
  const toggleDropdown = (menuName: string) => {
    setOpenDropdown(openDropdown === menuName ? null : menuName);
  };

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
      if (alarmDropdownRef.current && !alarmDropdownRef.current.contains(event.target as Node)) {
        setOpenAlarmDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 만료 예정/만료된 알림 데이터 가공
  const getExpirationAlerts = () => {
    const alerts: { driverName: string; type: string; endDate: string; isExpired: boolean }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // API 응답이 바로 배열로 옴 (axios interceptor에서 data 추출)
    const expiringDrivers = (Array.isArray(expiringDriversData) ? expiringDriversData : []) as DriverCode[];

    expiringDrivers.forEach((driver: DriverCode) => {
      const checkDate = (dateStr: string | Date | null | undefined, typeName: string) => {
        // null, undefined, 빈 문자열 무시
        if (dateStr === null || dateStr === undefined || dateStr === "") return;

        const endDate = new Date(dateStr);
        // 유효하지 않은 날짜 무시
        if (isNaN(endDate.getTime())) return;

        endDate.setHours(0, 0, 0, 0);

        if (endDate <= tomorrow) {
          alerts.push({
            driverName: driver.stDriverName || "알 수 없음",
            type: typeName,
            endDate: formatDate(endDate),
            isExpired: endDate < today,
          });
        }
      };

      checkDate(driver.stDriverLicenseEndDate, "면허");
      checkDate(driver.stDriverHazmatEduEndDate, "유해물질 교육");
      checkDate(driver.stDriverDangerEduEndDate, "위험물 교육");
    });

    return alerts;
  };

  const expirationAlerts = getExpirationAlerts();

  // TTS 기능: 로그인 시 만료 알림 읽어주기
  const [hasSpokenTTS, setHasSpokenTTS] = useState(false);

  // TTS 실행 함수
  const speakTTS = (message: string) => {
    if ("speechSynthesis" in window && message) {
      // 이전 음성 취소
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(message);
      utterance.lang = "ko-KR";
      utterance.rate = 0.9;
      utterance.pitch = 1;
      window.speechSynthesis.speak(utterance);
    }
  };

  useEffect(() => {
    // 알림이 있고, 아직 TTS를 실행하지 않았으며, 데이터가 로드되었을 때만 실행
    if (expirationAlerts.length > 0 && !hasSpokenTTS && Array.isArray(expiringDriversData) && expiringDriversData.length > 0) {
      // 세션 스토리지에서 TTS 실행 여부 확인 (로그인 세션당 1번만 실행)
      const ttsSpoken = sessionStorage.getItem("ttsSpoken");
      if (ttsSpoken) {
        setHasSpokenTTS(true);
        return;
      }

      const expiredCount = expirationAlerts.filter(a => a.isExpired).length;
      const expiringCount = expirationAlerts.length - expiredCount;

      let message = "";
      if (expiredCount > 0 && expiringCount > 0) {
        message = `주의! 만료된 기사 ${expiredCount}건, 만료 예정 기사 ${expiringCount}건이 있습니다. 확인해 주세요.`;
      } else if (expiredCount > 0) {
        message = `주의! 만료된 기사 ${expiredCount}건이 있습니다. 확인해 주세요.`;
      } else if (expiringCount > 0) {
        message = `알림! 만료 예정 기사 ${expiringCount}건이 있습니다. 확인해 주세요.`;
      }

      // 약간의 지연 후 TTS 실행 (페이지 로드 완료 후)
      setTimeout(() => {
        speakTTS(message);
      }, 1000);

      setHasSpokenTTS(true);
      sessionStorage.setItem("ttsSpoken", "true");
    }
  }, [expirationAlerts, hasSpokenTTS, expiringDriversData]);

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    window.location.href = "/";
  };

  return (
    <>
      <header className="shadow-sm relative">
        <div className="max-w-full mx-auto px-4">
          <div className="flex justify-between items-center">
            {/* 로고 및 네비게이션 메뉴 */}
            <div className="flex items-center space-x-8">
              {/* 로고 영역 */}
              <Link to="/" className="flex items-center">
                <img src="/images/titleFullLogo.svg" alt="logo" className="w-[190px] h-full" />
              </Link>

              {/* 네비게이션 메뉴 */}
              <nav className="flex items-center" ref={dropdownRef}>
                {menuItems.map(item => {
                  const isSingleMenu = !item.subMenus || item.subMenus.length === 0;
                  return (
                  <div key={item.name} className="relative">
                    <div
                      className={`group flex items-center py-[18px] pl-[26px] pr-4 transition-colors cursor-pointer ${
                        isMenuActive(item)
                          ? "bg-[#1E380B] text-[#FFF]"
                          : "text-[#1E380B] hover:bg-[#1E380B] hover:text-[#FFF]"
                      }`}
                      onClick={() => {
                        if (isSingleMenu && item.path) {
                          navigate(item.path);
                        } else {
                          toggleDropdown(item.name);
                        }
                      }}
                    >
                      <div
                        className={`text-base font-bold transition-colors flex items-center ${
                          isMenuActive(item) ? "text-[#FFF]" : "text-[#1E380B] group-hover:text-[#FFF]"
                        }`}
                      >
                        {item.name}
                        {isMenuActive(item) ? (
                          <img src="/icons/headerArrowWhite.svg" alt={item.name} />
                        ) : (
                          <>
                            <img src="/icons/headerArrowBlack.svg" alt={item.name} className="group-hover:hidden" />
                            <img
                              src="/icons/headerArrowWhite.svg"
                              alt={item.name}
                              className="hidden group-hover:block"
                            />
                          </>
                        )}
                      </div>
                    </div>

                    {/* 드롭다운 메뉴 */}
                    {openDropdown === item.name && (
                      <div className="absolute top-full left-0 bg-white border border-gray-200 shadow-lg z-[9999] min-w-[140px] whitespace-nowrap">
                        {item.subMenus?.map(subMenu => {
                          // 비밀번호 변경 모달
                          if (subMenu.path === "/system/changePassword") {
                            return (
                              <button
                                key={subMenu.path}
                                type="button"
                                className="block w-full px-[24px] py-[16px] text-base font-bold transition-colors border-b border-gray-100 last:border-b-0 text-gray-700 hover:bg-gray-50 text-left cursor-pointer"
                                onClick={() => {
                                  setOpenDropdown(null);
                                  setOpenChangePw(true);
                                }}
                              >
                                {subMenu.name}
                              </button>
                            );
                          }

                          // 내 메뉴 모달
                          if (subMenu.path === "/system/myMenu") {
                            return (
                              <button
                                key={subMenu.path}
                                type="button"
                                className="block w-full px-[24px] py-[16px] text-base font-bold transition-colors border-b border-gray-100 last:border-b-0 text-gray-700 hover:bg-gray-50 text-left cursor-pointer"
                                onClick={() => {
                                  setOpenDropdown(null);
                                  setOpenMyMenu(true);
                                }}
                              >
                                {subMenu.name}
                              </button>
                            );
                          }

                          // 준비중 메뉴
                          if (subMenu.isComingSoon) {
                            return (
                              <div
                                key={subMenu.path}
                                className="block px-[24px] py-[16px] text-base font-bold text-gray-400 border-b border-gray-100 last:border-b-0 cursor-default"
                                aria-disabled="true"
                              >
                                {subMenu.name}
                              </div>
                            );
                          }

                          // 일반 링크
                          return (
                            <Link
                              key={subMenu.path}
                              to={subMenu.path}
                              className={`block px-[24px] py-[16px] text-base font-bold transition-colors border-b border-gray-100 last:border-b-0 ${
                                location.pathname === subMenu.path
                                  ? "bg-[#1E380B] text-white"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                              onClick={() => setOpenDropdown(null)}
                            >
                              {subMenu.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })}
              </nav>
            </div>

            {/* 사용자 정보 영역 */}
            <div className="flex items-center space-x-4">
              {/* 알람 아이콘 with 뱃지 & 드롭다운 */}
              <div className="relative" ref={alarmDropdownRef}>
                <button
                  type="button"
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors relative"
                  onClick={() => setOpenAlarmDropdown(!openAlarmDropdown)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#666666"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {/* 알림 뱃지 */}
                  {expirationAlerts.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {expirationAlerts.length > 99 ? "99+" : expirationAlerts.length}
                    </span>
                  )}
                </button>

                {/* 알림 드롭다운 */}
                {openAlarmDropdown && (
                  <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 shadow-lg z-[9999] min-w-[320px] max-h-[400px] overflow-y-auto rounded-lg">
                    <div className="p-3 border-b border-gray-100 bg-gray-50">
                      <h3 className="font-bold text-gray-800">만료 예정 알림</h3>
                      <p className="text-sm text-gray-500">{expirationAlerts.length}건의 알림이 있습니다.</p>
                    </div>
                    {expirationAlerts.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">알림이 없습니다.</div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {expirationAlerts.map((alert, index) => (
                          <div
                            key={`${alert.driverName}-${alert.type}-${index}`}
                            className={`p-3 hover:bg-gray-50 ${alert.isExpired ? "bg-red-50" : ""}`}
                          >
                            <div className="flex items-start gap-2">
                              <span
                                className={`flex-shrink-0 w-2 h-2 mt-2 rounded-full ${
                                  alert.isExpired ? "bg-red-500" : "bg-yellow-500"
                                }`}
                              />
                              <div className="flex-1">
                                <p className="font-medium text-gray-800">
                                  {alert.driverName} - {alert.type}
                                </p>
                                <p className={`text-sm ${alert.isExpired ? "text-red-600" : "text-yellow-600"}`}>
                                  {alert.isExpired ? `만료됨 (${alert.endDate})` : `만료 예정 (${alert.endDate})`}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <span className="text-base font-normal text-[#666666]">{userInfo?.name} 님</span>
              <Button variant="white" className="px-3 py-2 font-normal" onClick={handleLogout}>
                로그아웃
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* 비밀번호 변경 모달 */}
      <ChangePasswordModal
        isOpen={openChangePw}
        onClose={() => setOpenChangePw(false)}
        onSubmit={async ({ currentPw, newPw }) => {
          // await api.changePassword({ currentPw, newPw });
        }}
      />

      {/* 내 메뉴 모달 */}
      <MyMenuModal isOpen={openMyMenu} onClose={() => setOpenMyMenu(false)} />
    </>
  );
}
